import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { headings, paragraphsWithStyle, readDocument } from "@/office/word/document";

export const lektionFormatvorlagenGrundlagen: Lesson = {
  id: "grundlagen",
  title: "Warum Formatvorlagen?",
  summary: "Der Unterschied zwischen „sieht aus wie eine Überschrift“ und „ist eine Überschrift“.",
  durationMinutes: 9,
  learningGoals: [
    "Den Unterschied zwischen direkter Formatierung und Formatvorlage erklären",
    "Formatvorlagen aus dem Katalog zuweisen",
    "Fließtext bewusst in der Vorlage „Standard“ belassen",
  ],
  steps: [
    {
      id: "vorlage-zuweisen",
      title: "Die erste Überschrift zuweisen",
      explanation: [
        "Im letzten Modul hast du Text von Hand formatiert: Schriftart, Größe, fett. " +
          "Für Word bleibt so eine Zeile aber ganz normaler Text, der zufällig größer " +
          "ist. Word weiß nicht, dass es sich um eine Überschrift handelt.",
        "Eine Formatvorlage ändert das. Sie ist ein gespeichertes Formatierungspaket mit " +
          "einem Namen – und dieser Name sagt Word, welche Rolle der Absatz im Dokument " +
          "spielt. Erst dadurch funktionieren Navigationsbereich, Inhaltsverzeichnis und " +
          "eine einheitliche Gestaltung über alle Kapitel hinweg.",
        "Den Katalog findest du auf der Registerkarte „Start“ in der Gruppe " +
          "„Formatvorlagen“. Ein Klick in den Absatz genügt – anders als bei der " +
          "Zeichenformatierung musst du nichts markieren.",
      ],
      keyPoints: [
        "Direkte Formatierung ändert das Aussehen, eine Formatvorlage die Bedeutung.",
        "Für Absatzvorlagen reicht der Cursor im Absatz, markieren ist nicht nötig.",
        "Tastenkürzel: Strg + Alt + 1 weist „Überschrift 1“ zu.",
      ],
      task: {
        instruction:
          "Schreibe eine Überschriftszeile und weise ihr die Formatvorlage " +
          "„Überschrift 1“ zu.",
        hint: "Cursor in die Zeile setzen, dann im Katalog „Formatvorlagen“ auf „Überschrift 1“ klicken.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length === 0) {
            return fail(
              "Im Dokument steht noch kein Text.",
              "Schreibe zuerst eine Zeile, die als Überschrift dienen soll.",
            );
          }

          const level1 = paragraphsWithStyle(snapshot, "Heading1");
          if (level1.length === 0) {
            return fail(
              "Noch kein Absatz mit der Vorlage „Überschrift 1“ gefunden.",
              "Achte darauf, wirklich den Katalog „Formatvorlagen“ zu benutzen und die Schrift nicht nur von Hand zu vergrößern.",
            );
          }
          return pass(`„${level1[0].text}“ ist jetzt eine echte Überschrift der Ebene 1.`);
        },
      },
    },
    {
      id: "zweite-ebene",
      title: "Der Formatvorlagen-Katalog",
      explanation: [
        "Im Katalog siehst du eine Vorschau der wichtigsten Vorlagen. Fährst du mit der " +
          "Maus darüber, zeigt Word die Wirkung direkt im Dokument an, ohne sie schon " +
          "zu übernehmen.",
        "Über den kleinen Pfeil rechts unten in der Gruppe öffnest du den vollständigen " +
          "Bereich „Formatvorlagen“ mit allen verfügbaren Vorlagen – dort lassen sie " +
          "sich später auch bearbeiten.",
      ],
      keyPoints: [
        "Strg + Alt + 1 / 2 / 3 weisen die Überschriftenebenen 1 bis 3 zu.",
        "Strg + Umschalt + N setzt einen Absatz zurück auf „Standard“.",
      ],
      task: {
        instruction:
          "Ergänze unter deiner Überschrift 1 eine weitere Zeile und weise ihr die " +
          "Vorlage „Überschrift 2“ zu.",
        hint: "Cursor in die neue Zeile setzen und Strg + Alt + 2 drücken.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          const ersteEbene1 = alle.find((eintrag) => eintrag.level === 1);
          const ebene2 = alle.find(
            (eintrag) =>
              eintrag.level === 2 &&
              ersteEbene1 !== undefined &&
              eintrag.paragraph.index > ersteEbene1.paragraph.index,
          );

          return fromCriteria(
            [
              criterion("Eine Überschrift der Ebene 1 ist vorhanden", Boolean(ersteEbene1)),
              criterion("Darunter steht eine Überschrift der Ebene 2", Boolean(ebene2)),
            ],
            {
              success: "Gut – dein Dokument hat jetzt zwei Gliederungsebenen.",
              failure: "Da fehlt noch etwas.",
              hint: "Die Überschrift 2 muss unterhalb der Überschrift 1 stehen, nicht darüber.",
            },
          );
        },
      },
    },
    {
      id: "standard-fliesstext",
      title: "Fließtext bleibt „Standard“",
      explanation: [
        "Für den normalen Fließtext gibt es die Vorlage „Standard“. Sie ist die Basis " +
          "aller anderen Vorlagen: Änderst du später „Standard“, wandern die Änderungen " +
          "automatisch in alle Vorlagen, die darauf aufbauen.",
        "Genau deshalb lohnt es sich, Fließtext wirklich als „Standard“ zu belassen und " +
          "nicht mit einer Überschriftenvorlage zu formatieren, nur weil die Schriftgröße " +
          "zufällig passt.",
      ],
      keyPoints: [
        "„Standard“ ist die Grundvorlage, von der die meisten anderen Vorlagen erben.",
        "Im deutschen Word heißt sie „Standard“, sprachunabhängig „Normal“.",
      ],
      task: {
        instruction:
          "Schreibe unter jede deiner beiden Überschriften mindestens einen Absatz " +
          "Fließtext in der Vorlage „Standard“.",
        hint: "Neue Absätze übernehmen nach einer Überschrift meist automatisch „Standard“. Falls nicht: Strg + Umschalt + N.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          const standard = paragraphsWithStyle(snapshot, "Normal");

          const mitText = alle.filter((eintrag) =>
            standard.some((absatz) => absatz.index > eintrag.paragraph.index),
          );
          const jedeUeberschriftHatText = alle.length >= 2 && mitText.length === alle.length;

          return fromCriteria(
            [
              criterion("Mindestens zwei Überschriften vorhanden", alle.length >= 2),
              criterion(
                `Mindestens zwei Absätze in der Vorlage „Standard“ (gefunden: ${standard.length})`,
                standard.length >= 2,
              ),
              criterion("Unter jeder Überschrift steht Fließtext", jedeUeberschriftHatText),
            ],
            {
              success: "Sehr gut – Überschriften und Fließtext sind jetzt sauber getrennt.",
              failure: "Der Fließtext fehlt noch an mindestens einer Stelle.",
              hint: "Prüfe im Katalog, welche Vorlage markiert ist, wenn der Cursor im Fließtext steht. Es muss „Standard“ sein.",
            },
          );
        },
      },
    },
  ],
};
